import {
  CloseCircleOutlined,
  FilterOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, Space } from 'antd';
import ruRU from 'antd/es/locale/ru_RU';
import Table from 'antd/es/table';
import { type FC } from 'react';

import {
  CustomAlert,
  EmptyComponentWithDataTestId,
  Error403Unauthorized,
  MultipleSelect,
} from '@app/components';
import { OFF } from '@app/constants';
import { REGISTRY_SEARCH_RULES } from '@app/constants/registrySearchRules';
import { getPaginationItemRenderWithDataTestIds } from '@app/utils';

import { BRANCHES_BLOCK } from '@pages/Branches/BranchesList/hooks/useBranchesList';

import { statusOptions } from '@pages/Branches/BranchesList/data/StatusOptions';
import {
  useBranchesList,
  useBranchesListEffects,
} from '@pages/Branches/BranchesList/hooks';

import '@pages/Branches/BranchesList/styles/Branches.desktop.scss';
import '@pages/Branches/BranchesList/styles/Branches.tablet.scss';
import '@pages/Branches/BranchesList/styles/Branches.mobile.scss';

export const BranchesList: FC = () => {
  const {
    form,
    isMobile,
    toolbarClassName,
    hideFilters,
    columns,
    isLoading,
    branchesData,
    rowsCount,
    rowsPerPage,
    pageNumber,
    handleTableChange,
    submitForm,
    handleReset,
    errorType,
    serverError,
    setServerError,
    initialStatusValue,
    chainsOptions,
    countriesOptions,
    usedCitiesOptions,
    selectedChains,
    setSelectedChains,
    selectedCountries,
    setSelectedCountries,
    selectedCities,
    setSelectedCities,
    selectedStatuses,
    setSelectedStatuses,
    chainsData,
    countriesData,
    usedCitiesData,
    notificationContextHolder,
    hasUserRightToModelView,
    rowClassName,
    isFiltersHiddenHandler,
  } = useBranchesList();

  useBranchesListEffects({ isFiltersHiddenHandler });

  if (!hasUserRightToModelView) {
    return <Error403Unauthorized />;
  }

  return (
    <div className={BRANCHES_BLOCK}>
      {notificationContextHolder}

      <div className={`${BRANCHES_BLOCK}__header`}>
        <Form
          form={form}
          autoComplete={OFF}
          onFinish={submitForm}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 30 }}
          style={{ marginBottom: 10 }}
          layout="vertical"
          initialValues={{
            status: initialStatusValue,
            chains: chainsData,
            countries: countriesData,
            cities: usedCitiesData,
          }}
        >
          <Form.Item
            name="search"
            wrapperCol={{ span: 16 }}
            rules={REGISTRY_SEARCH_RULES.search}
          >
            <Input
              placeholder="Поиск по наименованию филиала"
              addonBefore={<SearchOutlined />}
              data-testid="branch-name-input"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space
              align="end"
              size="large"
              direction="horizontal"
              wrap
              style={{ width: '100%' }}
              className={toolbarClassName}
            >
              <Space
                size="large"
                direction="horizontal"
                wrap
                style={{ width: '100%' }}
                className={`${BRANCHES_BLOCK}__selectors`}
              >
                {isMobile && (
                  <Button
                    type="primary"
                    className={`${BRANCHES_BLOCK}__show-filters`}
                    onClick={hideFilters}
                  >
                    <FilterOutlined /> Фильтры
                  </Button>
                )}

                <MultipleSelect
                  optionDataTestId="network-item"
                  placeHolderDataTestId="network-search-input"
                  dataTestId="network-select"
                  className={`${BRANCHES_BLOCK}__selector`}
                  placeholder="Все сети"
                  allOptions={chainsOptions}
                  width={100}
                  selectedOption={selectedChains}
                  setSelectedOptions={setSelectedChains}
                />

                <MultipleSelect
                  optionDataTestId="country-item"
                  placeHolderDataTestId="country-search-input"
                  dataTestId="country-select"
                  className={`${BRANCHES_BLOCK}__selector`}
                  placeholder="Все страны"
                  allOptions={countriesOptions}
                  width={100}
                  selectedOption={selectedCountries}
                  setSelectedOptions={setSelectedCountries}
                />

                <MultipleSelect
                  optionDataTestId="city-item"
                  placeHolderDataTestId="city-search-input"
                  dataTestId="city-select"
                  className={`${BRANCHES_BLOCK}__selector`}
                  placeholder="Все города"
                  allOptions={usedCitiesOptions}
                  width={100}
                  selectedOption={selectedCities}
                  setSelectedOptions={setSelectedCities}
                />

                <MultipleSelect
                  optionDataTestId="status-item"
                  placeHolderDataTestId="status-search-input"
                  dataTestId="status-select"
                  className={`${BRANCHES_BLOCK}__selector`}
                  placeholder="Все статусы"
                  allOptions={statusOptions}
                  width={100}
                  selectedOption={selectedStatuses}
                  setSelectedOptions={setSelectedStatuses}
                />

                <Form.Item
                  style={{
                    display: 'inline-block',
                    marginBottom: 0,
                  }}
                  className={`${BRANCHES_BLOCK}__buttons-container`}
                >
                  {serverError && errorType && (
                    <CustomAlert
                      errorType={errorType}
                      serverError={serverError}
                      setServerError={setServerError}
                    />
                  )}

                  <Space
                    className={`${BRANCHES_BLOCK}__buttons-tablet`}
                    size="middle"
                  >
                    <Button
                      htmlType="reset"
                      onClick={handleReset}
                      className={`${BRANCHES_BLOCK}__button`}
                      data-testid={isMobile ? 'reset-filters' : ''}
                    >
                      <CloseCircleOutlined />
                      Сброс
                    </Button>

                    <Button
                      type="primary"
                      htmlType="submit"
                      className={`${BRANCHES_BLOCK}__button`}
                      data-testid={isMobile ? 'search-by-filters' : ''}
                    >
                      <SearchOutlined />
                      Поиск
                    </Button>
                  </Space>
                </Form.Item>
              </Space>

              <Space
                className={`${BRANCHES_BLOCK}__buttons-desktop`}
                size="middle"
              >
                <Button
                  htmlType="reset"
                  onClick={handleReset}
                  data-testid={isMobile ? '' : 'reset-filters'}
                >
                  <CloseCircleOutlined />
                  Сброс
                </Button>

                <Button
                  type="primary"
                  htmlType="submit"
                  data-testid={isMobile ? '' : 'search-by-filters'}
                >
                  <SearchOutlined />
                  Поиск
                </Button>
              </Space>
            </Space>
          </Form.Item>
        </Form>
      </div>

      <Table
        columns={columns}
        loading={isLoading}
        dataSource={branchesData}
        rowClassName={rowClassName}
        locale={{
          emptyText: <EmptyComponentWithDataTestId />,
        }}
        pagination={{
          itemRender: getPaginationItemRenderWithDataTestIds,
          className: 'branches-list-pagination',
          size: isMobile ? 'small' : 'default',
          showSizeChanger: true,
          position: isMobile ? ['bottomCenter'] : ['bottomLeft'],
          total: rowsCount,
          defaultCurrent: pageNumber,
          showTotal: (total, range) => (
            <span data-testid="pagination-info">{`Показаны ${range[0]}-${range[1]} из ${total}`}</span>
          ),
          pageSize: rowsPerPage,
          onChange: handleTableChange,
          locale: ruRU.Pagination,
        }}
      />
    </div>
  );
};
